import { db } from '../../data/base';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { secureGetItem } from '../../data/utils';
import { mapFormToDatabase } from '../utils/catalogueModificationUtils';
import { encryptCataloguePassword } from '../utils/cataloguePasswordUtils';
import { getDoc, setDoc } from 'firebase/firestore';
import { sanitizeForFirestore } from './catalogueBaseService';


// Utility: genera una stringa CSS border-radius da 4 valori
export function getCornerRadiusString(topLeft, topRight, bottomRight, bottomLeft) {
    return `${topLeft || 0}px ${topRight || 0}px ${bottomRight || 0}px ${bottomLeft || 0}px`;
}

// Mapping completo di formData in struttura Firestore
export function mapFormDataToDraftedCatalogue(formData) {

    // Prima di tutto, usa il mapping centralizzato per convertire form -> database
    const mappedData = mapFormToDatabase(formData);

    const result = {
        catalogues_page_style: {
            // Colori personalizzati
            primaryColor: formData.customization?.primaryColor || null,
            secondaryColor: formData.customization?.secondaryColor || null,
            backgroundColor: formData.customization?.backgroundColor || '#FFFFFF',
            actionButtonColor: formData.customization?.actionButtonColor || null,
            bannerBackgroundColor: formData.customization?.bannerBackgroundColor || null,
            // Border radius per le card
            cardCornerTopLeft: formData.customization?.cardCornerTopLeft ?? 0,
            cardCornerTopRight: formData.customization?.cardCornerTopRight ?? 0,
            cardCornerBottomRight: formData.customization?.cardCornerBottomRight ?? 0,
            cardCornerBottomLeft: formData.customization?.cardCornerBottomLeft ?? 0,
            // Border radius per i pulsanti
            buttonCornerTopLeft: formData.customization?.buttonCornerTopLeft ?? 0,
            buttonCornerTopRight: formData.customization?.buttonCornerTopRight ?? 0,
            buttonCornerBottomRight: formData.customization?.buttonCornerBottomRight ?? 0,
            buttonCornerBottomLeft: formData.customization?.buttonCornerBottomLeft ?? 0,
            // Border radius per il banner
            bannerCornerTopLeft: formData.customization?.bannerCornerTopLeft ?? 0,
            bannerCornerTopRight: formData.customization?.bannerCornerTopRight ?? 0,
            bannerCornerBottomRight: formData.customization?.bannerCornerBottomRight ?? 0,
            bannerCornerBottomLeft: formData.customization?.bannerCornerBottomLeft ?? 0,
            // Logo e posizione del logo
            catalogueLogo: formData.banner?.logo || null,
            catalogueLogoPosition: formData.banner?.logoPosition || 'center',
            // ✅ USA I CAMPI CORRETTI PER LE ALTEZZE DAL MAPPING CENTRALIZZATO
            coverHeightDesktop: formData.banner?.coverHeightDesktop || 100,
            coverHeightMobile: formData.banner?.coverHeightMobile || 100,
            // Immagini del banner
            coverImg: formData.banner?.bannerImage || null,
            coverImgMobile: formData.banner?.bannerImageMobile || null,
            isLogoVisible: formData.banner?.isLogoVisible ?? true,
            isTitleVisible: formData.banner?.isTitleVisible ?? true,
            // ✅ MANTIENI ANCHE I CAMPI UI PER LA COMPATIBILITÀ  
            heightOption: formData.banner?.heightOption || formData.banner?.coverHeightDesktop?.toString() || "100",
            heightMobileOption: formData.banner?.heightMobileOption || formData.banner?.coverHeightMobile?.toString() || "100",
            coverColor: null,
            coverTemplate: null,
        },
        codePath: null,
        coverImage: null,
        dataConsumption: null,
        glassesNumber: null,
        hdrEnvironment: null,
        lightIntensityExponent: null,
        list_service: null,
        nameCatalog: mappedData.nameCatalog || null,
        noBackgroundLogo: null,
        showSingleDotList: true,
        skin: null,
        storageConsumption: null,
        storage_consumption: null,
        takePicTemplate: null,
        urlLogo: formData.banner?.logo || null,
        webDomain: mappedData.webDomain || null,
        webUrl: null,

        // --- ✅ CAMPI SEO E PERMISSIONS USA IL MAPPING CENTRALIZZATO ---
        titleSeo: mappedData.titleSeo || null,
        list_keywords: mappedData.list_keywords || [],
        featuredImage: mappedData.featuredImage || null,
        metaDescription: mappedData.metaDescription || null,
        descriptionIt: mappedData.descriptionIt || null,
        descriptionEn: mappedData.descriptionEn || null,
        descriptionFr: mappedData.descriptionFr || null,
        descriptionEs: mappedData.descriptionEs || null,
        descriptionDe: mappedData.descriptionDe || null,
        isLoginRequired: mappedData.isLoginRequired ?? false,
        isIframeOnlyAccess: mappedData.isIframeOnlyAccess ?? false,
        allowedDomain: mappedData.allowedDomain || null,
        loginPassword: mappedData.loginPassword || null,
        isPublic: mappedData.isPublic ?? true,
    };

    return result;
}

// --- FIREBASE STORAGE UTILS ---
const storage = getStorage();

export async function uploadCatalogueAsset(file, filename) {
    const storageRef = ref(storage, `/catalogues/assets/${filename}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
}

// Prepara logo/banner/bannerMobile: se sono File li carica e sostituisce con la URL
export async function prepareAssetsAndSave(formData) {
    const newFormData = { ...formData };

    // Funzione di supporto per estrarre l'URL da vari tipi di oggetti immagine
    const extractImageUrl = (imageObj) => {
        if (!imageObj) return null;

        // Se è una stringa, è già un URL
        if (typeof imageObj === 'string') return imageObj;

        // Se ha una proprietà url, usa quella
        if (typeof imageObj.url === 'string') return imageObj.url;

        // Se ha una proprietà thumbUrl, usa quella
        if (typeof imageObj.thumbUrl === 'string') return imageObj.thumbUrl;

        return null;
    };

    // --- LOGO ---
    if (newFormData.banner?.logo) {
        newFormData.banner = { ...newFormData.banner };

        // Se è un File object diretto
        if (newFormData.banner.logo instanceof File) {
            newFormData.banner.logo = await uploadCatalogueAsset(newFormData.banner.logo, `logo_${Date.now()}_${newFormData.banner.logo.name}`);
        }
        // Se è un oggetto Ant Design Upload con originFileObj
        else if (typeof newFormData.banner.logo === 'object' && newFormData.banner.logo.originFileObj instanceof File) {
            newFormData.banner.logo = await uploadCatalogueAsset(newFormData.banner.logo.originFileObj, `logo_${Date.now()}_${newFormData.banner.logo.originFileObj.name}`);
        }
        // Se è un oggetto con file property (come {file: File, fileList: Array})
        else if (typeof newFormData.banner.logo === 'object' && newFormData.banner.logo.file instanceof File) {
            newFormData.banner.logo = await uploadCatalogueAsset(newFormData.banner.logo.file, `logo_${Date.now()}_${newFormData.banner.logo.file.name}`);
        }
        // Se è un oggetto con URL ma non è un File (come quelli creati da urlToFileObject)
        else {
            const logoUrl = extractImageUrl(newFormData.banner.logo);
            if (logoUrl) {
                newFormData.banner.logo = logoUrl;
            }
        }
    }
    // --- BANNER DESKTOP ---
    if (newFormData.banner?.bannerImage) {
        newFormData.banner = { ...newFormData.banner };

        // Se è un File object diretto
        if (newFormData.banner.bannerImage instanceof File) {
            newFormData.banner.bannerImage = await uploadCatalogueAsset(newFormData.banner.bannerImage, `banner_${Date.now()}_${newFormData.banner.bannerImage.name}`);
        }
        // Se è un oggetto Ant Design Upload con originFileObj
        else if (typeof newFormData.banner.bannerImage === 'object' && newFormData.banner.bannerImage.originFileObj instanceof File) {
            newFormData.banner.bannerImage = await uploadCatalogueAsset(newFormData.banner.bannerImage.originFileObj, `banner_${Date.now()}_${newFormData.banner.bannerImage.originFileObj.name}`);
        }
        // Se è un oggetto con file property (come {file: File, fileList: Array})
        else if (typeof newFormData.banner.bannerImage === 'object' && newFormData.banner.bannerImage.file instanceof File) {
            newFormData.banner.bannerImage = await uploadCatalogueAsset(newFormData.banner.bannerImage.file, `banner_${Date.now()}_${newFormData.banner.bannerImage.file.name}`);
        }
        // Se è un oggetto con URL ma non è un File (come quelli creati da urlToFileObject)
        else {
            const bannerUrl = extractImageUrl(newFormData.banner.bannerImage);
            if (bannerUrl) {
                newFormData.banner.bannerImage = bannerUrl;
            }
        }
    }

    // --- BANNER MOBILE ---
    if (newFormData.banner?.bannerImageMobile) {
        newFormData.banner = { ...newFormData.banner };

        // Se è un File object diretto
        if (newFormData.banner.bannerImageMobile instanceof File) {
            newFormData.banner.bannerImageMobile = await uploadCatalogueAsset(newFormData.banner.bannerImageMobile, `bannerMobile_${Date.now()}_${newFormData.banner.bannerImageMobile.name}`);
        }
        // Se è un oggetto Ant Design Upload con originFileObj
        else if (typeof newFormData.banner.bannerImageMobile === 'object' && newFormData.banner.bannerImageMobile.originFileObj instanceof File) {
            newFormData.banner.bannerImageMobile = await uploadCatalogueAsset(newFormData.banner.bannerImageMobile.originFileObj, `bannerMobile_${Date.now()}_${newFormData.banner.bannerImageMobile.originFileObj.name}`);
        }
        // Se è un oggetto con file property (come {file: File, fileList: Array})
        else if (typeof newFormData.banner.bannerImageMobile === 'object' && newFormData.banner.bannerImageMobile.file instanceof File) {
            newFormData.banner.bannerImageMobile = await uploadCatalogueAsset(newFormData.banner.bannerImageMobile.file, `bannerMobile_${Date.now()}_${newFormData.banner.bannerImageMobile.file.name}`);
        }
        // Se è un oggetto con URL ma non è un File (come quelli creati da urlToFileObject)
        else {
            const bannerMobileUrl = extractImageUrl(newFormData.banner.bannerImageMobile);
            if (bannerMobileUrl) {
                newFormData.banner.bannerImageMobile = bannerMobileUrl;
            }
        }
    }
    // --- FEATURED IMAGE (SEO) ---
    if (newFormData.seo?.featuredImage) {
        newFormData.seo = { ...newFormData.seo };

        // Se è un File object diretto
        if (newFormData.seo.featuredImage instanceof File) {
            newFormData.seo.featuredImage = await uploadCatalogueAsset(newFormData.seo.featuredImage, `featured_${Date.now()}_${newFormData.seo.featuredImage.name}`);
        }
        // Se è un oggetto Ant Design Upload con originFileObj
        else if (typeof newFormData.seo.featuredImage === 'object' && newFormData.seo.featuredImage.originFileObj instanceof File) {
            newFormData.seo.featuredImage = await uploadCatalogueAsset(newFormData.seo.featuredImage.originFileObj, `featured_${Date.now()}_${newFormData.seo.featuredImage.originFileObj.name}`);
        }
        // Se è un oggetto con file property (come {file: File, fileList: Array})
        else if (typeof newFormData.seo.featuredImage === 'object' && newFormData.seo.featuredImage.file instanceof File) {
            newFormData.seo.featuredImage = await uploadCatalogueAsset(newFormData.seo.featuredImage.file, `featured_${Date.now()}_${newFormData.seo.featuredImage.file.name}`);
        }
        // Se è un oggetto con URL ma non è un File (come quelli creati da urlToFileObject)
        else {
            const featuredImageUrl = extractImageUrl(newFormData.seo.featuredImage);
            if (featuredImageUrl) {
                newFormData.seo.featuredImage = featuredImageUrl;
            }
        }
    }

    return newFormData;
}

// --- API CALL FIRESTORE ---

export async function saveDraftedCatalogue(formData) {
    const readyFormData = await prepareAssetsAndSave(formData);
    const data = mapFormDataToDraftedCatalogue(readyFormData);

    // Sanitize data to remove any File objects before saving to Firestore
    const sanitizedData = sanitizeForFirestore(data);

    const docRef = await addDoc(collection(db, 'DraftedCatalogues'), sanitizedData);
    const draftId = docRef.id;

    // Aggiorna la lista dei drafted catalogues nel profilo utente
    try {
        let email = null;
        try {
            email = secureGetItem('email');
        } catch (e) { }
        if (email) {
            email = email.toLowerCase();
            const profileRef = doc(db, 'Profiles', email);
            const profileSnap = await getDoc(profileRef);
            let listDrafted = [];
            if (profileSnap.exists()) {
                const profileData = profileSnap.data();
                listDrafted = Array.isArray(profileData.list_drafted_catalogues)
                    ? profileData.list_drafted_catalogues
                    : [];
            }
            // Aggiungi solo se non già presente
            if (!listDrafted.includes(draftId)) {
                listDrafted.push(draftId);
                await setDoc(profileRef, { list_drafted_catalogues: listDrafted }, { merge: true });
            }
        }
    } catch (err) {
        console.error('Errore aggiornando list_drafted_catalogues nel profilo:', err);
    }
    return draftId;
}

export async function updateDraftedCatalogue(draftId, formData) {
    const readyFormData = await prepareAssetsAndSave(formData);
    const data = mapFormDataToDraftedCatalogue(readyFormData);

    // Sanitize data to remove any File objects before saving to Firestore
    const sanitizedData = sanitizeForFirestore(data);

    const draftRef = doc(db, 'DraftedCatalogues', draftId);
    await updateDoc(draftRef, sanitizedData);
    return draftId;
}
